import { Client, IMessage, StompSubscription } from '@stomp/stompjs';
import SockJS from 'sockjs-client';
import { BASE_URL } from '../constants/constants';

export type ConnectionState = 'DISCONNECTED' | 'CONNECTING' | 'CONNECTED' | 'RECONNECTING';

export type WebSocketEventType = 'MESSAGE_NEW' | 'LOBBY_UPDATE' | 'CHAT_CLOSED' | 'CHAT_APPROVED' | 'CHAT_DECLINED';

export interface WebSocketEvent<T = any> {
  type: WebSocketEventType;
  chatId: number;
  payload: T;
}

export interface TeleChatMessagePayload {
  messageId: number;
  senderType: 'PATIENT' | 'DOCTOR' | 'SYSTEM';
  senderId: number;
  messageText: string | null;
  fileUrl: string | null;
  fileName: string | null;
  fileSizeBytes: number | null;
  fileType: string | null;
  isPreScreening: boolean;
  createdAt: string;
}

export interface TeleChatListPayload {
  chatId: number;
  status: string;
  paymentStatus: string;
  consultationPrice: number;
  lastMessage: string | null;
  lastMessageAt: string | null;
  unreadCount: number;
  createdAt: string;
  doctor?: {
    id: number;
    fullName: string;
    speciality: string;
  };
  patient?: {
    id: number;
    fullName: string;
    age: number;
    gender: string;
  };
}

type ConnectionStateCallback = (state: ConnectionState) => void;
type MessageCallback = (event: WebSocketEvent<TeleChatMessagePayload>) => void;
type LobbyCallback = (event: WebSocketEvent<TeleChatListPayload>) => void;

/**
 * Decode a JWT and report whether it is expired (or unparseable). A 5s skew
 * treats a token that's about to expire as already expired.
 */
function isTokenExpired(token: string | null): boolean {
  if (!token) return true;
  try {
    const part = token.split('.')[1];
    if (!part) return true;
    const base64 = part.replace(/-/g, '+').replace(/_/g, '/');
    const pad = base64.length % 4 === 0 ? '' : '='.repeat(4 - (base64.length % 4));
    const payload = JSON.parse(atob(base64 + pad));
    if (typeof payload.exp !== 'number') return false;
    return payload.exp * 1000 <= Date.now() + 5000;
  } catch {
    return true;
  }
}

class WebSocketService {
  private client: Client | null = null;
  private connectionState: ConnectionState = 'DISCONNECTED';
  private connectionStateListeners: Set<ConnectionStateCallback> = new Set();
  private chatSubscriptions: Map<number, StompSubscription> = new Map();
  private lobbySubscription: StompSubscription | null = null;
  private chatMessageCallbacks: Map<number, Set<MessageCallback>> = new Map();
  private lobbyCallbacks: Set<LobbyCallback> = new Set();
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 10;
  private token: string | null = null;

  /**
   * Connect to WebSocket server with JWT token
   */
  connect(token: string): void {
    // Already connected OR a connect attempt is already in flight. `active` is
    // true from activate() until deactivate(), so this also covers the
    // still-connecting window — without it a second connect() would spin up a
    // second client, orphan the first (which keeps its own reconnect loop), and
    // the two would repeatedly re-authenticate.
    if (this.client && (this.client.connected || this.client.active)) {
      if (import.meta.env.DEV) {
        console.log('[WebSocket] Connect ignored — already connected/connecting');
      }
      return;
    }

    // Never open a socket with an already-expired token — it would just be
    // rejected in a tight reconnect loop. Force re-auth instead (same as REST).
    if (isTokenExpired(token)) {
      if (import.meta.env.DEV) {
        console.warn('[WebSocket] Access token expired — forcing re-auth instead of connecting');
      }
      this.token = null;
      this.setConnectionState('DISCONNECTED');
      this.forceReauth();
      return;
    }

    this.token = token;
    this.setConnectionState('CONNECTING');

    // Build WebSocket URL - use SockJS endpoint
    const wsUrl = `${BASE_URL}ws`;

    this.client = new Client({
      // Use SockJS as the WebSocket factory
      webSocketFactory: () => new SockJS(wsUrl),

      // Pass JWT token in STOMP headers
      connectHeaders: {
        token: token,
      },

      // Heartbeat settings
      heartbeatIncoming: 10000,
      heartbeatOutgoing: 10000,

      // Reconnect settings
      reconnectDelay: 5000,

      onConnect: () => {
        if (import.meta.env.DEV) {
          console.log('[WebSocket] Connected');
        }
        this.reconnectAttempts = 0;
        this.setConnectionState('CONNECTED');

        // Resubscribe to lobby if there are listeners
        if (this.lobbyCallbacks.size > 0) {
          this.subscribeToLobbyInternal();
        }

        // Resubscribe to active chats
        this.chatMessageCallbacks.forEach((_, chatId) => {
          this.subscribeToChatInternal(chatId);
        });
      },

      onDisconnect: () => {
        if (import.meta.env.DEV) {
          console.log('[WebSocket] Disconnected');
        }
        this.setConnectionState('DISCONNECTED');
      },

      onStompError: (frame) => {
        console.error('[WebSocket] STOMP error:', frame.headers['message']);
        if (isTokenExpired(this.token)) {
          this.giveUp();
        } else {
          this.setConnectionState('DISCONNECTED');
        }
      },

      onWebSocketClose: () => {
        // Lost an already-established connection — reset the counter and let
        // stompjs reconnect.
        if (this.connectionState === 'CONNECTED') {
          if (import.meta.env.DEV) {
            console.log('[WebSocket] Connection lost, will attempt to reconnect...');
          }
          this.reconnectAttempts = 0;
          this.setConnectionState('RECONNECTING');
          return;
        }

        // A (re)connect attempt failed before it was established.
        // If the token is expired, stop immediately instead of hammering.
        if (isTokenExpired(this.token)) {
          this.giveUp();
          return;
        }

        // Otherwise treat as a transient failure and cap the retries.
        this.reconnectAttempts += 1;
        if (this.reconnectAttempts >= this.maxReconnectAttempts) {
          this.giveUp();
        }
      },
    });

    this.client.activate();
  }

  /**
   * Disconnect from WebSocket server
   */
  disconnect(): void {
    if (this.client) {
      // Unsubscribe from all
      this.chatSubscriptions.forEach((sub) => sub.unsubscribe());
      this.chatSubscriptions.clear();
      this.chatMessageCallbacks.clear();

      if (this.lobbySubscription) {
        this.lobbySubscription.unsubscribe();
        this.lobbySubscription = null;
      }
      this.lobbyCallbacks.clear();

      this.client.deactivate();
      this.client = null;
    }
    this.token = null;
    this.setConnectionState('DISCONNECTED');
    if (import.meta.env.DEV) {
      console.log('[WebSocket] Disconnected');
    }
  }

  /**
   * Subscribe to messages for a specific chat
   */
  subscribeToChatMessages(chatId: number, callback: MessageCallback): () => void {
    // Add callback to the set for this chat
    if (!this.chatMessageCallbacks.has(chatId)) {
      this.chatMessageCallbacks.set(chatId, new Set());
    }
    this.chatMessageCallbacks.get(chatId)!.add(callback);

    // Subscribe if connected and not already subscribed
    if (this.client?.connected && !this.chatSubscriptions.has(chatId)) {
      this.subscribeToChatInternal(chatId);
    }

    // Return unsubscribe function
    return () => {
      const callbacks = this.chatMessageCallbacks.get(chatId);
      if (callbacks) {
        callbacks.delete(callback);
        if (callbacks.size === 0) {
          // No more listeners, unsubscribe from STOMP
          const sub = this.chatSubscriptions.get(chatId);
          if (sub) {
            sub.unsubscribe();
            this.chatSubscriptions.delete(chatId);
          }
          this.chatMessageCallbacks.delete(chatId);
        }
      }
    };
  }

  /**
   * Subscribe to lobby updates (chat list changes)
   */
  subscribeToLobbyUpdates(callback: LobbyCallback): () => void {
    this.lobbyCallbacks.add(callback);

    // Subscribe if connected and not already subscribed
    if (this.client?.connected && !this.lobbySubscription) {
      this.subscribeToLobbyInternal();
    }

    // Return unsubscribe function
    return () => {
      this.lobbyCallbacks.delete(callback);
      if (this.lobbyCallbacks.size === 0 && this.lobbySubscription) {
        this.lobbySubscription.unsubscribe();
        this.lobbySubscription = null;
      }
    };
  }

  /**
   * Add listener for connection state changes
   */
  onConnectionStateChange(callback: ConnectionStateCallback): () => void {
    this.connectionStateListeners.add(callback);
    // Immediately call with current state
    callback(this.connectionState);

    return () => {
      this.connectionStateListeners.delete(callback);
    };
  }

  /**
   * Get current connection state
   */
  getConnectionState(): ConnectionState {
    return this.connectionState;
  }

  /**
   * Check if connected
   */
  isConnected(): boolean {
    return this.connectionState === 'CONNECTED';
  }

  /**
   * Force reconnect - used when app returns from background
   */
  reconnect(): void {
    const token = this.token;

    // If we have an existing client, force disconnect it synchronously
    if (this.client) {
      try {
        // forceDisconnect is synchronous and doesn't wait for STOMP disconnect frame
        this.client.forceDisconnect();
      } catch {
        // Ignore errors - connection might already be dead
      }
      this.client = null;
    }

    // Small delay to let the old connection fully close
    setTimeout(() => {
      if (token) {
        this.connect(token);
      }
    }, 100);
  }

  // Private methods

  /**
   * Stop the reconnect loop. If the token is expired/gone, force re-auth
   * (consistent with the REST layer's handling of a 401); otherwise it was a
   * transient network failure, so just stay disconnected — a later
   * visibilitychange/online event will call reconnect() and try again.
   */
  private giveUp(): void {
    const expired = isTokenExpired(this.token);
    this.reconnectAttempts = 0;
    if (this.client) {
      try {
        this.client.deactivate();
      } catch {
        // already dead
      }
      this.client = null;
    }
    this.setConnectionState('DISCONNECTED');
    if (expired) {
      this.token = null;
      this.forceReauth();
    }
  }

  /**
   * Signal that the session is no longer valid. AuthContext listens for this
   * and logs the user out.
   */
  private forceReauth(): void {
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new Event('auth:unauthorized'));
    }
  }

  private setConnectionState(state: ConnectionState): void {
    this.connectionState = state;
    this.connectionStateListeners.forEach((callback) => callback(state));
  }

  private subscribeToChatInternal(chatId: number): void {
    if (!this.client?.connected) return;

    const destination = `/user/queue/chat/${chatId}`;
    const subscription = this.client.subscribe(destination, (message: IMessage) => {
      try {
        const event: WebSocketEvent<TeleChatMessagePayload> = JSON.parse(message.body);
        if (import.meta.env.DEV) {
          console.log('[WebSocket] Chat message received:', event);
        }

        const callbacks = this.chatMessageCallbacks.get(chatId);
        callbacks?.forEach((callback) => callback(event));
      } catch (e) {
        console.error('[WebSocket] Failed to parse chat message:', e);
      }
    });

    this.chatSubscriptions.set(chatId, subscription);
    if (import.meta.env.DEV) {
      console.log(`[WebSocket] Subscribed to chat ${chatId}`);
    }
  }

  private subscribeToLobbyInternal(): void {
    if (!this.client?.connected) return;

    const destination = '/user/queue/lobby';
    this.lobbySubscription = this.client.subscribe(destination, (message: IMessage) => {
      try {
        const event: WebSocketEvent<TeleChatListPayload> = JSON.parse(message.body);
        if (import.meta.env.DEV) {
          console.log('[WebSocket] Lobby update received:', event);
        }

        this.lobbyCallbacks.forEach((callback) => callback(event));
      } catch (e) {
        console.error('[WebSocket] Failed to parse lobby update:', e);
      }
    });

    if (import.meta.env.DEV) {
      console.log('[WebSocket] Subscribed to lobby updates');
    }
  }
}

// Export singleton instance
export const webSocketService = new WebSocketService();
